import { Injectable } from '@angular/core';
import {UtilsService} from "../shared/utils.service";
import { CURRENCIES, Currency } from '../constants/currencies.constants';
import moment from "moment";
import { AppService } from '../app.service';
import { CurrentTimezoneStateService } from '../shared/current-timezone-state.service';

@Injectable({
  providedIn: 'root'
})
export class AssetsService {

  readonly STATUSES: Record<string, string> = {
    active: 'Active/Operational',
    standby: 'Standby',
    under_maintenance: 'Under Maintenance',
    under_repair: 'Under Repair',
    damaged: 'Damaged',
    out_of_service: 'Out of Service',
    in_storage: 'In Storage/Stock',
    stolen: 'Stolen/Missing',
    decommissioned: 'Decommissioned',
    sold: 'Sold',
    scrapped: 'Scrapped/Disposed'
  };

  readonly LIFECYCLE_END_STATUSES: string[] = [
    'stolen',
    'decommissioned',
    'sold',
    'scrapped'
  ];

  readonly CONDITION_RATINGS: Record<string, string> = {
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    poor: 'Poor',
    unsafe: 'Unsafe'
  };

  readonly OWNER_TYPES: Record<string, string> = {
    owned: 'Owned',
    leased: 'Leased',
    hired: 'Hired',
    contractor_owned: this.utils.getLangTerm('contractors.singular', 'Contractor') + ' Owned'
  };

  readonly NOT_OWNED_TYPES: string[] = [
    'leased',
    'hired',
    'contractor_owned'
  ];

  readonly RECURRING_PAYMENT_OWNER_TYPES: string[] = [
    'leased',
    'hired'
  ];

  readonly COST_FREQUENCIES: Record<string, string> = {
    daily: 'Daily',
    weekly: 'Weekly',
    fortnightly: 'Fortnightly',
    monthly: 'Monthly',
    yearly: 'Yearly'
  };

  readonly CURRENCIES = CURRENCIES;

  constructor(
    private app: AppService,
    private utils: UtilsService,
    private cTmzState: CurrentTimezoneStateService
  ) { }

  /**
   * Validate Asset Code Prefix (3–6 alphanumeric characters).
   *
   * @param {string | null | undefined} value
   * @return {boolean}
   */
  codePrefixValid(value: string | null | undefined): boolean {
    if (!value) {
      return false;
    }

    const normalized = value.trim().toUpperCase();

    // Must be 3–6 characters, letters or numbers only
    return /^[a-zA-Z0-9]{3,6}$/.test(normalized);
  }

  /**
   * Get the label for the status based on the provided value.
   *
   * @param {string | null | undefined} value - The status value to retrieve the label for.
   * @return {string} The label corresponding to the provided status value.
   */
  getStatusLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.STATUSES, value);
  }

  /**
   * Get the label for the condition rating based on the provided value.
   *
   * @param {string} value - The value for which the label needs to be retrieved.
   * @return {string} - The label corresponding to the provided condition rating value.
   */
  getConditionLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.CONDITION_RATINGS, value);
  }

  /**
   * Get the label for the owner type based on the provided value.
   *
   * @param {string | null | undefined} value - The value representing the owner type.
   * @return {string} The label corresponding to the provided owner type value.
   */
  getOwnerTypeLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.OWNER_TYPES, value);
  }

  /**
   * Get the label for the cost frequency based on the provided value.
   *
   * @param {string | null | undefined} value - The value for which the label needs to be retrieved.
   * @return {string} The label corresponding to the provided cost frequency value.
   */
  getCostFrequencyLabel(value: string | null | undefined): string {
    return this.utils.getLabel(this.COST_FREQUENCIES, value);
  }

  /**
   * Check if lifecycle end fields are allowed for the given status.
   *
   * @param {string | null | undefined} status
   * @return {boolean}
   */
  isLifecycleEndStatus(status: string | null | undefined): boolean {
    return !!status && this.LIFECYCLE_END_STATUSES.includes(status);
  }

  /**
   * Checks if the specified owner type is not owned.
   *
   * @param {string|null|undefined} owner_type - The type of owner to check.
   * @return {boolean} Returns true if the owner type is not owned, false otherwise.
   */
  isNotOwned(owner_type: string | null | undefined): boolean {
    return !!owner_type && this.NOT_OWNED_TYPES.includes(owner_type);
  }

  /**
   * Check if the given owner type is a recurring payment owner type.
   *
   * @param {string | null | undefined} owner_type - The type of owner for the payment.
   * @return {boolean} - True if the owner type is a recurring payment owner type, false otherwise.
   */
  isRecurringPayment(owner_type: string | null | undefined): boolean {
    return !!owner_type && this.RECURRING_PAYMENT_OWNER_TYPES.includes(owner_type);
  }

  /**
   * Retrieves the currency object associated with the provided currency code.
   *
   * @param {string | null | undefined} code - The currency code to lookup
   *
   * @return {Currency | undefined} The currency object corresponding to the given code, or undefined if no match is found.
   */
  getCurrency(code: string | null | undefined): Currency | undefined {
    if (!code) {
      return undefined;
    }

    return this.CURRENCIES.find(currency => currency.code === code);
  }

  /**
   * Retrieves the currency symbol based on the provided currency code.
   *
   * @param {string|null|undefined} code - The currency code to retrieve the symbol for.
   * @return {string} - The currency symbol corresponding to the given code. If the code is not found, an empty string is returned.
   */
  getCurrencySymbol(code: string | null | undefined): string {
    return this.getCurrency(code)?.symbol || '';
  }

  /**
   * Retrieve the minor units of a currency based on the currency code.
   *
   * @param {string} code - The currency code for which to retrieve the minor units.
   * @return {number} The minor units of the specified currency. Returns 2 if the currency code is not found or is invalid.
   */
  getCurrencyMinorUnits(code: string | null | undefined): number {
    return this.getCurrency(code)?.minorUnits ?? 2;
  }

  /**
   * Retrieves the step size for a currency based on the number of minor units defined for that currency code.
   *
   * @param {string|null|undefined} code - The currency code for which the step size needs to be determined.
   * @return {string} - The step size for the currency, indicated as a string value.
   */
  getCurrencyStep(code: string | null | undefined): string {
    const minor_units = this.getCurrencyMinorUnits(code);

    if (minor_units <= 0) {
      return '1';
    }

    return `0.${'0'.repeat(minor_units - 1)}1`;
  }

  /**
   * Retrieves a placeholder value for the given currency code.
   *
   * @param {string | null | undefined} code - The currency code to get the placeholder for.
   * @return {string} The placeholder value for the currency code. Returns '0' if the minor units value is less than or equal to 0,
   * otherwise returns a string representing a fractional value based on the number of minor units.
   */
  getCurrencyPlaceholder(code: string | null | undefined): string {
    const minor_units = this.getCurrencyMinorUnits(code);

    if (minor_units <= 0) {
      return '0';
    }

    return `0.${'0'.repeat(minor_units)}`;
  }

  /**
   * Validates if the provided currency amount is valid based on the given currency code.
   *
   * @param {number | string | null | undefined} value - The currency amount to be validated.
   * @param {string | null | undefined} code - The currency code used for validation.
   * @return {boolean} Returns true if the currency amount is valid, false otherwise.
   */
  isValidCurrencyAmount(value: number | string | null | undefined, code: string | null | undefined): boolean {
    if (value === null || value === '' || typeof value === 'undefined') {
      return true;
    }

    const parsed_value = Number(value);

    if (isNaN(parsed_value) || parsed_value < 0) {
      return false;
    }

    const minor_units = this.getCurrencyMinorUnits(code);
    const decimal_part = parsed_value.toString().split('.')[1] || '';

    return decimal_part.length <= minor_units;
  }

  /**
   * Normalizes a currency amount based on the provided currency code.
   *
   * @param {number | string | null | undefined} value - The value to be normalized.
   * @param {string | null | undefined} code - The currency code used to determine minor units.
   * @return {number | null} The normalized currency amount or null if value is invalid.
   */
  normalizeCurrencyAmount(
    value: number | string | null | undefined,
    code: string | null | undefined
  ): string | null {
    if (value === null || value === '' || typeof value === 'undefined') {
      return null;
    }

    const parsedValue = Number(value);

    if (isNaN(parsedValue)) {
      return null;
    }

    const minorUnits = this.getCurrencyMinorUnits(code);

    // Whole numbers stay whole.
    if (Number.isInteger(parsedValue)) {
      return parsedValue.toString();
    }

    // Decimal values are formatted to currency minor units.
    return parsedValue.toFixed(minorUnits);
  }

  /**
   * Get cost amount prefix combining currency code and symbol.
   * Example: "NZD $"
   *
   * @param code
   * @return string
   */
  getCostAmountPrefix(code: string | null | undefined): string {
    if (!code) {
      return '';
    }

    const currency = this.getCurrency(code);

    if (!currency) {
      return code; // fallback to code only
    }

    const symbol = currency.symbol || '';

    return symbol ? `${currency.code} ${symbol}` : currency.code;
  }

  /**
   * Formats a cost amount for display using the currency minor units,
   * with the currency code and symbol shown on the left.
   *
   * Example:
   *   NZD + 1200.5 => "NZD $ 1,200.50"
   *  JPY + 1200 => "JPY ¥ 1,200"
   *
   * @param {number | string | null | undefined} value - The cost amount to format.
   * @param {string | null | undefined} code - The currency code.
   * @return {string} The formatted display value.
   */
  getCostAmountDisplay(
    value: number | string | null | undefined,
    code: string | null | undefined
  ): string {
    if (value === null || value === '' || typeof value === 'undefined') {
      return '';
    }

    const parsed_value = Number(value);

    if (isNaN(parsed_value)) {
      return '';
    }

    const currency = this.getCurrency(code);
    const minor_units = this.getCurrencyMinorUnits(code);

    const formatted_amount = parsed_value.toLocaleString(undefined, {
      minimumFractionDigits: minor_units,
      maximumFractionDigits: minor_units
    });

    if (!currency) {
      return code ? `${code} ${formatted_amount}` : formatted_amount;
    }

    const currency_suffix = currency.symbol
      ? `${currency.code} ${currency.symbol}`
      : currency.code;

    return `${currency_suffix} ${formatted_amount}`;
  }

  /**
   * Returns a color code based on the status value.
   *
   * @param {string | null | undefined} value - The status value to determine the color for.
   * @return {string | null} - The color code corresponding to the status value, or null if no corresponding color found.
   */
  getStatusColor(value: string | null | undefined): string | null {
    switch (value) {
      case 'active':
        return '#2e7d32'; // green

      case 'standby':
      case 'under_maintenance':
      case 'under_repair':
      case 'damaged':
      case 'out_of_service':
      case 'in_storage':
        return '#ef6c00'; // orange

      case 'stolen':
      case 'decommissioned':
      case 'sold':
      case 'scrapped':
        return '#c62828'; // red

      default:
        return null;
    }
  }

  /**
   * Returns a color code based on the condition value.
   *
   * @param {string | null | undefined} value - The condition value to determine the color for.
   * @return {string | null} - The color code corresponding to the condition value, or null if no corresponding color found.
   */
  getConditionColor(value: string | null | undefined): string {
    switch (value) {
      case 'excellent': return '#2e7d32';
      case 'good': return '#66bb6a';
      case 'fair': return '#f9a825';
      case 'poor': return '#ef6c00';
      case 'unsafe': return '#c62828';
      default: return null;
    }
  }

  /**
   * Generate a suggested Asset Code Prefix from an Asset Type/Sub Type name.
   * The generated prefix is 3–6 alphanumeric characters.
   *
   * Examples:
   * "Excavators" => "EXC"
   * "Mobile Scaffold" => "MOSCAF"
   * "Power Tools" => "POTOOL"
   * "LED Site Lighting Tower" => "LEDSIT"
   * "Pi" => "PIX"
   *
   * @param name
   * @return string
   */
  generateCodePrefixFromAssetType(name: string | null | undefined): string {
    if (!name) {
      return '';
    }

    const cleanName = name
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, ' ');

    if (!cleanName) {
      return '';
    }

    const words = cleanName.split(' ').filter(Boolean);

    let prefix = '';

    // Single-word asset type.
    if (words.length === 1) {
      prefix = words[0].substring(0, 3);
    } else {
      // Multi-word asset type.
      prefix = words[0].substring(0, 3);

      for (let i = 1; i < words.length; i++) {
        if (prefix.length >= 6) {
          break;
        }

        const remaining = 6 - prefix.length;

        prefix += words[i].substring(0, remaining);
      }
    }

    prefix = prefix.toUpperCase();

    // Ensure minimum length of 3 characters.
    while (prefix.length < 3) {
      prefix += 'X';
    }

    return prefix.substring(0, 6);
  }

  /**
   * Returns the CSS class for an Asset's Maintenance Records due date.
   *
   * @param {number} dueAt The earliest Maintenance Record due date as a Unix timestamp.
   * @param {string | null | undefined} timezone The optional timezone to use for date comparison.
   * @return {string} The CSS class representing the Maintenance Record due state.
   */
  getMaintenanceRecordDueClass(dueAt: number, timezone?: string | null | undefined): string {
    const selectedTimezone = timezone ?? this.cTmzState.getCurrentTimezone();

    const dueDate = moment
      .unix(dueAt)
      .tz(selectedTimezone)
      .startOf('day');

    const today = moment
      .tz(selectedTimezone)
      .startOf('day');

    const diffDays = dueDate.diff(today, 'days');

    if (diffDays <= 0) {
      return 'maintenance-record-due-overdue';
    }

    if (diffDays <= 7) {
      return 'maintenance-record-due-soon';
    }

    return 'maintenance-record-due';
  }

  /**
   * Returns the tooltip text for an Asset's earliest active Maintenance Record due date.
   *
   * @param {number} dueAt The Maintenance Record due date as a Unix timestamp.
   * @param {string | null | undefined} timezone The optional timezone to use for date comparison.
   * @return {string} The tooltip text describing the Maintenance Record due status.
   */
  getMaintenanceRecordDueTooltip(dueAt: number, timezone?: string | null | undefined): string {
    const selectedTimezone = timezone ?? this.cTmzState.getCurrentTimezone();

    const dueDate = moment
      .unix(dueAt)
      .tz(selectedTimezone)
      .startOf('day');

    const today = moment
      .tz(selectedTimezone)
      .startOf('day');

    const diffDays = dueDate.diff(today, 'days');

    if (diffDays < 0) {
      return 'Maintenance overdue.';
    }

    if (diffDays === 0) {
      return 'Maintenance due today.';
    }

    if (diffDays <= 7) {
      return `Maintenance due in ${diffDays} day${diffDays === 1 ? '' : 's'}.`;
    }

    return 'Maintenance scheduled.';
  }

}
